import { NIGHTMARES } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { cosA, idiv, iatan2, imax, imin, isqrt, mulBp, sinA, withinRange, dist2 } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, LitterRules, LitterState, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { attackHero, stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { beamTrace, type BeamBoard } from './litter-layout.ts';
import { sendaCue } from './senda.ts';
import { pasArrived, pasBlocked, pasBusy, pasHalt, pasHold, pasMarkAt, pasOrder, pasResult, pasTick } from './pasarela.ts';
import { nevAloft, nevBusy, nevGoal, nevHold, nevMarkAt, nevOrder, nevResult, nevTick } from './nieve.ts';
import { ermBusy, ermGoal, ermHold, ermHoly, ermMarkAt, ermOrder, ermResult, ermTick } from './ermita.ts';

/**
 * The stretcher (docs/sendas/clerigo.md, chapter 1, «La camilla»; §3, «La camilla» and «Las lapas de tinta»; the
 * grounds in battle/litter-layout.ts). Piet in front and Ludo behind carry Tessa; a lantern hangs from its pole. It goes
 * on along its way while the hero keeps within `near` and both carriers stand; a carrier down sets it on the ground
 * until the hero stands by them (`lift`). The nightmares go for the stretcher, not the hero: each one at it lands a blow
 * a second on the nearest of its three (one by the hero fights the hero). Tessa heals only with the cleric's heals, when
 * they land on a hero beside her (battle/combat.ts `healHero` calls `litterMend`). Lost with Tessa out, or both
 * carriers down at once; won at the end of its way (the chapel: with its lamp lit).
 *
 * - The lower street (`street`): stalkers out of the alleys ahead of it, seen only inside the lantern's light
 *   (battle/combat.ts `isRevealed` asks `litterSeen`); soot crows off the roofs at the hero.
 * - The chapel (`chapel`): the stretcher rests; the lantern hangs on a hook and its beam goes off through the mirrors
 *   (§4: a hook or a mirror worked by standing by it; the beam burns the ink in it); stalkers for the stretcher, in the
 *   dark while the lantern hangs.
 * - The slope (`slope`, the test): the limpets come out at each bend as the stretcher nears it (and one now and then
 *   between), crawl to it and, stuck, drain Tessa; a holy blow peels one (battle/combat.ts `damageEnemy` calls
 *   `litterHoly`) and it jumps on the hero who struck it, draining the hero until it dies. Soot crows peck the carriers.
 *
 * Cost: three people and a handful of nightmares, looked at once a tick; the blows and the drains once a second; the
 * beam worked out again only when the lantern or a mirror moves.
 */

type St = LitterState;
const fresh = (r: LitterRules): St => {
  const a = r.way[0] as { x: number; y: number };
  const b = (r.way[1] ?? { x: a.x, y: a.y - 1000 }) as { x: number; y: number };
  return {
    seg: 0,
    along: 0,
    x: a.x,
    y: a.y,
    facing: iatan2(b.y - a.y, b.x - a.x),
    moving: false,
    done: -1,
    acc: 0,
    lift: [0, 0],
    next: r.foes?.first ?? 0x7fffffff,
    crow: r.crows?.first ?? 0x7fffffff,
    trickle: r.limpet?.trickle ?? 0x7fffffff,
    burst: 0,
    dens: 0,
    limpets: [],
    lantern: -1,
    turns: [...(r.mirrors?.start ?? [])],
    job: -1,
    held: 0,
    beam: [],
    hits: 0,
    lit: -1,
    pas: null,
    nev: null,
  };
};

/** The mark of the lantern going back to the stretcher (the chapel's marks: the hooks, then the mirrors). */
export const LITTER_BACK = 9;
export const MIRROR_MARK = 2;

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const carriers = (s: BattleState): (SendaNpc | undefined)[] => [person(s, 'piet'), person(s, 'ludo')];

/** The chapel's board for the beam. */
const boardOf = (r: LitterRules): BeamBoard | null => (r.mirrors ? { hooks: r.mirrors.hooks, mirrors: r.mirrors.mirrors, lamp: r.mirrors.lamp, walls: r.mirrors.walls, radius: r.mirrors.radius } : null);

/** Where the lantern is: on its pole over Tessa, or on a hook. */
export const lanternAt = (s: BattleState): { x: number; y: number } | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w) return null;
  const hk = w.lantern >= 0 ? r.mirrors?.hooks[w.lantern] : undefined;
  return hk ? hexToWorld(hk.q, hk.r) : { x: w.x, y: w.y };
};

/** Whether a point is inside the lantern's light. */
const inLight = (s: BattleState, r: LitterRules, x: number, y: number): boolean => {
  const p = lanternAt(s);
  return !!p && withinRange(x, y, p.x, p.y, r.light);
};

/** What a stalker needs to be seen: the lantern's light (whatever isn't invisible is seen). */
export const litterSeen = (s: BattleState, e: Enemy): boolean => {
  // (Chapter 3: the ash watcher up in the storm.)
  if (nevAloft(s, e)) return false;
  if (!NIGHTMARES[e.kind].invisible) return true;
  const r = s.rules?.senda?.litter;
  const p = lanternAt(s);
  return !!r && !!p && withinRange(e.x, e.y, p.x, p.y, r.light);
};

/** Tessa's life moves by ten-thousandths of a blow: what has added up to a whole blow lands (or comes back). */
const settle = (s: BattleState, w: St, t: SendaNpc): void => {
  while (w.acc >= 10000 && t.hp > 0) {
    w.acc -= 10000;
    t.hp--;
    ev(s, { t: 'senda', kind: t.hp > 0 ? 'hit' : 'down', npc: t.id, x: t.x, y: t.y });
  }
  while (w.acc <= -10000 && t.hp < t.maxHp && t.hp > 0) {
    w.acc += 10000;
    t.hp++;
  }
  if (t.hp >= t.maxHp && w.acc < 0) w.acc = 0;
  if (t.hp <= 0) w.acc = 0;
};

/**
 * A heal of the cleric's on a hero (battle/combat.ts `healHero`): if the hero stands beside Tessa, she gets the same
 * share of her life (the heal's share of the hero's), whatever the hero's own life.
 */
export const litterMend = (s: BattleState, h: Hero, amount: number): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const t = person(s, 'tessa');
  if (!r || !w || !t || t.hp <= 0 || h.stats.maxHp <= 0 || !withinRange(h.x, h.y, t.x, t.y, r.mendReach)) return;
  const share = imin(10000, idiv(amount * 10000, h.stats.maxHp));
  const back = mulBp(share * t.maxHp, r.mendBp);
  if (back <= 0) return;
  w.acc -= back;
  settle(s, w, t);
  ev(s, { t: 'senda', kind: 'mended', npc: t.id, x: h.x, y: h.y, n: idiv(back, 10000) });
};

/** A holy blow on a limpet stuck on the stretcher peels it: it jumps on the one who struck. */
export const litterHoly = (s: BattleState, e: Enemy): void => {
  const w = s.senda?.litter;
  if (!w || (!w.limpets.length && !w.erm)) return;
  // (Chapter 4: a limpet on one of the nave's mirrors: battle/ermita.ts.)
  if (w?.erm && ermHoly(s, e)) return;
  const l = w.limpets.find((x) => x.id === e.id);
  if (!l || l.on !== 1) return;
  l.on = 3;
  l.t = s.tick;
  ev(s, { t: 'senda', kind: 'peel', npc: '', x: e.x, y: e.y });
};

/** Sends the hero to a hook, a mirror or the stretcher (the chapel's marks). */
export const litterOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const M = r?.mirrors;
  // (Chapter 3: the braziers, the bases, the pile, the frozen mirrors: battle/nieve.ts.)
  if (r?.snow && w) return nevOrder(s, h, at);
  // (Chapter 4: the lamps, the mirrors and dividers, Aud's mirror: battle/ermita.ts.)
  if (r?.hermit && w) return ermOrder(s, h, at);
  // (Chapter 2: the walkway's posts, the stretcher told to go: battle/pasarela.ts.)
  if (r && w && !M && w.pas) return pasOrder(s, h, at);
  if (!r || !w || !M || w.lit >= 0) return;
  const p = at === LITTER_BACK ? (w.lantern >= 0 ? { x: w.x + 900, y: w.y } : null) : at < MIRROR_MARK ? M.hooks[at]?.stand : M.mirrors[at - MIRROR_MARK]?.stand;
  if (!p) return;
  w.job = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** The point of the mark the hero works (the stretcher, a hook, a mirror). */
export const litterMarkAt = (s: BattleState, at: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const M = r?.mirrors;
  if (r?.snow) return nevMarkAt(s, at);
  if (r?.hermit) return ermMarkAt(s, at);
  if (r && w && !M) return pasMarkAt(s, at);
  if (!r || !w || !M) return null;
  if (at === LITTER_BACK) return { x: w.x, y: w.y };
  const c = at < MIRROR_MARK ? M.hooks[at] : M.mirrors[at - MIRROR_MARK];
  return c ? hexToWorld(c.q, c.r) : null;
};

/** The beam worked out again: its corners for the drawing, the mirrors it went by, the lamp lit. */
const rebeam = (s: BattleState, r: LitterRules, w: St, cue: (c: string) => void): void => {
  const B = boardOf(r);
  if (!B) return;
  const b = beamTrace(B, w.lantern, w.turns);
  w.beam = [];
  for (const p of b.pts) {
    const c = hexToWorld(p.q, p.r);
    w.beam.push(c.x, c.y);
  }
  w.hits = new Set(b.by).size;
  if (b.lit && w.lit < 0) {
    w.lit = s.tick;
    const c = hexToWorld(B.lamp.q, B.lamp.r);
    ev(s, { t: 'senda', kind: 'lamp', npc: '', x: c.x, y: c.y });
    cue('lit');
  }
};

/** The hook or mirror the hero was sent to, worked by standing still by it. */
const work = (s: BattleState, r: LitterRules, w: St, h: Hero | undefined, cue: (c: string) => void): void => {
  const M = r.mirrors;
  if (!M || w.job < 0 || !h || w.lit >= 0) return;
  const p = litterMarkAt(s, w.job);
  if (!p) return;
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, M.reach)) {
    w.held = 0;
    return;
  }
  if (++w.held < M.stand) return;
  w.held = 0;
  const at = w.job;
  w.job = -1;
  if (at === LITTER_BACK) {
    if (w.lantern < 0) return;
    w.lantern = -1;
    ev(s, { t: 'senda', kind: 'hung', npc: '', x: w.x, y: w.y, n: -1 });
  } else if (at < MIRROR_MARK) {
    if (w.lantern === at) return;
    w.lantern = at;
    ev(s, { t: 'senda', kind: 'hung', npc: '', x: p.x, y: p.y, n: at });
    cue('dark');
  } else {
    const i = at - MIRROR_MARK;
    w.turns[i] = ((w.turns[i] ?? 0) + 1) % 3;
    ev(s, { t: 'senda', kind: 'mirror', npc: '', x: p.x, y: p.y, n: i });
    cue(`turn${imin(9, (s.senda?.nums.turns ?? 0) + 1)}`);
    if (s.senda) s.senda.nums.turns = (s.senda.nums.turns ?? 0) + 1;
  }
  rebeam(s, r, w, cue);
};

/** Along its way: the stretcher moves on while the hero keeps near and both carriers stand. */
const carry = (s: BattleState, r: LitterRules, w: St, h: Hero | undefined, cue: (c: string) => void): void => {
  w.moving = false;
  if (r.kind === 'chapel' || r.kind === 'frost' || r.kind === 'corral' || r.kind === 'vigil' || r.kind === 'nave' || w.done >= 0 || !h) return;
  const [p, l] = carriers(s);
  if (!p || !l || p.hp <= 0 || l.hp <= 0) return;
  if (!withinRange(h.x, h.y, w.x, w.y, r.near)) return;
  // (Chapter 2: waiting in a chapel or told to wait, Piet hanging; never onto ink or a torn plank: battle/pasarela.ts.)
  const P = w.pas;
  if (P && pasHalt(r, P)) return;
  let left = r.pace;
  let moved = false;
  while (left > 0) {
    const a = r.way[w.seg] as { x: number; y: number };
    const b = r.way[w.seg + 1];
    if (!b) {
      w.done = s.tick;
      ev(s, { t: 'senda', kind: 'end', npc: '', x: w.x, y: w.y });
      cue('gate');
      return;
    }
    const len = imax(1, isqrt(dist2(a.x, a.y, b.x, b.y)));
    const step = imin(left, len - w.along);
    const along = w.along + step;
    const f = iatan2(b.y - a.y, b.x - a.x);
    const nx = a.x + idiv((b.x - a.x) * along, len);
    const ny = a.y + idiv((b.y - a.y) * along, len);
    if (P && pasBlocked(r, P, nx, ny, f)) break;
    moved = true;
    w.along = along;
    left -= step;
    w.facing = f;
    w.x = nx;
    w.y = ny;
    if (w.along >= len) {
      w.seg++;
      w.along = 0;
      if (P && pasArrived(s, r, w, cue)) break;
    }
  }
  w.moving = moved;
  if (!moved) return;
  if (w.seg * 2 >= r.way.length - 1) cue('half');
};

/** Its three where the stretcher is: Tessa on it, Piet at the front pole, Ludo at the back. */
const placePeople = (s: BattleState, w: St): void => {
  const fx = idiv(cosA(w.facing) * 650, 10000);
  const fy = idiv(sinA(w.facing) * 650, 10000);
  const t = person(s, 'tessa');
  const [p, l] = carriers(s);
  for (const [n, k] of [
    [t, 0],
    [p, 1],
    [l, -1],
  ] as const) {
    if (!n) continue;
    const x = w.x + fx * k;
    const y = w.y + fy * k;
    n.moving = w.moving && n.hp > 0;
    n.facing = w.facing;
    n.x = x;
    n.y = y;
  }
};

/** A carrier down: the hero by them gets them up. */
const lifts = (s: BattleState, r: LitterRules, w: St, h: Hero | undefined): void => {
  carriers(s).forEach((c, i) => {
    if (!c || c.hp > 0) {
      w.lift[i] = 0;
      return;
    }
    if (!h || !withinRange(h.x, h.y, c.x, c.y, r.liftReach)) {
      w.lift[i] = 0;
      return;
    }
    w.lift[i] = (w.lift[i] ?? 0) + 1;
    if ((w.lift[i] as number) < r.lift) return;
    w.lift[i] = 0;
    c.hp = imax(1, mulBp(c.maxHp, r.liftBp));
    ev(s, { t: 'senda', kind: 'lifted', npc: c.id, x: c.x, y: c.y });
  });
};

/** A nightmare fights the hero (one by it), else goes for the stretcher's nearest. */
const prey = (s: BattleState, r: LitterRules, e: Enemy, h: Hero | undefined): { hero: Hero | null; npc: SendaNpc | null } => {
  const crow = e.kind === 'crow';
  if (h && (crow ? r.crows?.at === 'hero' : withinRange(h.x, h.y, e.x, e.y, r.hold))) return { hero: h, npc: null };
  // A hero out of the lantern's light is the stalkers' first: in the dark they see it and it doesn't see them.
  if (h && e.kind === 'stalker' && !inLight(s, r, h.x, h.y)) return { hero: h, npc: null };
  let best: SendaNpc | null = null;
  let bd = 0x7fffffff;
  for (const n of s.senda?.npcs ?? []) {
    // (Only the stretcher's three: the muleteer of chapter 2 is nobody's; gargoyles go for the carriers.)
    if (n.hp <= 0 || ((crow || e.kind === 'gargoyle') && n.id === 'tessa') || (n.id !== 'tessa' && n.id !== 'piet' && n.id !== 'ludo')) continue;
    const d = dist2(e.x, e.y, n.x, n.y);
    if (d < bd) {
      bd = d;
      best = n;
    }
  }
  if (!best && h) return { hero: h, npc: null };
  return { hero: null, npc: best };
};

/** The blows of the second: each nightmare at one of the three lands one on it. */
const blows = (s: BattleState, r: LitterRules, w: St, h: Hero | undefined, cue: (c: string) => void): void => {
  const t = person(s, 'tessa');
  for (const e of s.enemies) {
    if (e.hp <= 0 || w.limpets.some((l) => l.id === e.id) || (w.pas && pasBusy(s, e)) || (w.nev && nevBusy(s, e)) || (w.erm && ermBusy(s, e))) continue;
    // (Crows peck: a blow every other second.)
    if (e.kind === 'crow' && (s.tick / r.blow.every) % 2 === 1) continue;
    const { npc } = prey(s, r, e, h);
    if (!npc || !withinRange(e.x, e.y, npc.x, npc.y, r.blow.range)) continue;
    if (npc === t) {
      w.acc += 10000;
      continue;
    }
    if (npc.hp <= 0) continue;
    npc.hp--;
    ev(s, { t: 'senda', kind: npc.hp > 0 ? 'hit' : 'down', npc: npc.id, x: npc.x, y: npc.y });
    if (npc.hp <= 0) {
      ev(s, { t: 'senda', kind: 'set', npc: npc.id, x: w.x, y: w.y, n: npc.id === 'piet' ? 0 : 1 });
      cue('down1');
    }
  }
  // The limpets: on the stretcher they drain Tessa; on the hero, the hero.
  const L = r.limpet;
  if (L) {
    for (const l of w.limpets) {
      if (l.on === 1 && t) w.acc += L.drainBp * t.maxHp;
      if (l.on === 2 && h) {
        const e = s.enemies.find((x) => x.id === l.id);
        damageHero(s, h, mulBp(h.stats.maxHp, L.heroBp), e ?? null, 'shadow', true);
      }
    }
  }
  if (t) settle(s, w, t);
};

const scaled = (s: BattleState, e: Enemy): Enemy => {
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

/** Stalkers out of the dens near the stretcher (ahead of it, or behind when nothing is ahead). */
const stalkers = (s: BattleState, r: LitterRules, w: St): void => {
  const F = r.foes;
  if (!F || s.waveT < w.next || w.done >= 0 || w.lit >= 0) return;
  w.next = s.waveT + (F.every > 0 ? F.every : rngRange(s.rng, F.gap[0], F.gap[1]));
  let pick: { x: number; y: number; at: number } | null = r.kind === 'chapel' ? (r.dens[rngInt(s.rng, r.dens.length)] ?? null) : null;
  let bd = pick ? -1 : 0x7fffffff;
  for (const d of r.dens) {
    const ahead = d.at - w.seg;
    if (r.kind === 'chapel' || ahead < 2 || ahead > F.ahead + 3) continue;
    const k = Math.abs(ahead - F.ahead);
    if (k < bd) {
      bd = k;
      pick = d;
    }
  }
  if (!pick) for (const d of r.dens) if (d.at < w.seg && (!pick || d.at > pick.at)) pick = d;
  if (!pick) return;
  const n = rngRange(s.rng, F.n[0], F.n[1]);
  for (let k = 0; k < n; k++) scaled(s, spawnEnemy(s, 'stalker', 1, false, 0, pick.x + k * 300, pick.y + rngRange(s.rng, -200, 200)));
};

/** A flock of soot crows off the roofs (the street: at the hero; the slope: at the carriers). */
const crows = (s: BattleState, r: LitterRules, w: St, h: Hero | undefined): void => {
  const C = r.crows;
  if (!C || s.waveT < w.crow || w.done >= 0) return;
  w.crow = s.waveT + C.every;
  const at = h ?? { x: w.x, y: w.y };
  const side = rngInt(s.rng, 2) === 0 ? -1 : 1;
  for (let k = 0; k < C.n; k++) scaled(s, spawnEnemy(s, 'crow', 1, false, 0, at.x + side * (6000 + k * 400), at.y - 2500 + k * 600));
};

/** The limpets out of the ground: at each bend as the stretcher nears it, and one now and then between. */
const limpets = (s: BattleState, r: LitterRules, w: St, cue: (c: string) => void): void => {
  const L = r.limpet;
  if (!L || w.done >= 0) return;
  const alive = w.limpets.length;
  const out = (x: number, y: number): void => {
    const e = spawnEnemy(s, 'dragger', 1, false, 0, x, y, L.hp);
    e.speed = L.speed;
    e.anim = 'idle';
    w.limpets.push({ id: e.id, on: 0, t: s.tick });
    ev(s, { t: 'senda', kind: 'limpetOut', npc: '', x, y });
  };
  const b = L.bends[w.burst];
  if (b && (w.seg >= b.at - 1 || withinRange(w.x, w.y, b.x, b.y, L.near))) {
    w.burst++;
    // Out of the ground round the stretcher itself, a hex and a bit off: they are on it in a moment.
    for (let k = 0; k < L.burst && alive + k < L.cap; k++) {
      const a = idiv(k * 1024, L.burst) + rngRange(s.rng, -60, 60);
      const d = rngRange(s.rng, 1100, 1500);
      out(w.x + idiv(cosA(a) * d, 10000), w.y + idiv(sinA(a) * d, 10000));
    }
    cue(`bend${w.burst}`);
  }
  // The ground by the legs too, as it goes by: a couple at a time.
  for (const d of r.dens) {
    if (d.at !== w.seg || w.limpets.length >= L.cap) continue;
    const k = r.dens.indexOf(d);
    if (k < w.dens) continue;
    if (!withinRange(d.x, d.y, w.x, w.y, L.near)) continue;
    w.dens = k + 1;
    for (let j = 0; j < 2; j++) {
      const a = rngRange(s.rng, 0, 1023);
      out(w.x + idiv(cosA(a) * 1300, 10000), w.y + idiv(sinA(a) * 1300, 10000));
    }
  }
  if (s.waveT >= w.trickle) {
    w.trickle = s.waveT + L.trickle;
    if (alive >= L.cap) return;
    let pick: { x: number; y: number; at: number } | null = null;
    for (const d of r.dens) if (d.at >= w.seg && withinRange(d.x, d.y, w.x, w.y, 7000) && (!pick || dist2(d.x, d.y, w.x, w.y) < dist2(pick.x, pick.y, w.x, w.y))) pick = d;
    if (pick) out(pick.x, pick.y);
  }
};

/** The beam's burn: what stands in it takes its holy light a second. */
const burn = (s: BattleState, r: LitterRules, w: St): void => {
  const M = r.mirrors;
  if (!M || w.lantern < 0 || w.beam.length < 4) return;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    for (let i = 0; i + 3 < w.beam.length; i += 2) {
      const ax = w.beam[i] as number;
      const ay = w.beam[i + 1] as number;
      const bx = w.beam[i + 2] as number;
      const by = w.beam[i + 3] as number;
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      let k = len2 > 0 ? idiv(((e.x - ax) * dx + (e.y - ay) * dy) * 1000, len2) : 0;
      k = imax(0, imin(1000, k));
      if (dist2(e.x, e.y, ax + idiv(dx * k, 1000), ay + idiv(dy * k, 1000)) <= 450 * 450) {
        damageEnemy(s, e, M.burn, { pid: '', type: 'holy' });
        break;
      }
    }
  }
};

export const litterTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.litter;
  const st = s.senda;
  if (!r || !st) return;
  let w = st.litter;
  if (!w) {
    w = st.litter = fresh(r);
    if (r.kind === 'chapel') w.facing = 768;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (s.started) {
    // The dead limpets let go.
    if (w.limpets.length) w.limpets = w.limpets.filter((l) => s.enemies.some((e) => e.id === l.id && e.hp > 0));
    carry(s, r, w, h, cue);
    lifts(s, r, w, h);
    work(s, r, w, h, cue);
    pasTick(s, r, w, h, cue);
    nevTick(s, r, w, h, cue);
    ermTick(s, r, w, h, cue);
    stalkers(s, r, w);
    crows(s, r, w, h);
    limpets(s, r, w, cue);
    if (s.tick % r.blow.every === 0) {
      blows(s, r, w, h, cue);
      burn(s, r, w);
      // The first stalker seen in the light.
      if (r.foes && !st.cues?.includes('stalker') && s.enemies.some((e) => e.hp > 0 && e.kind === 'stalker' && litterSeen(s, e))) cue('stalker');
      if (r.mirrors && h && !st.cues?.includes('inscription')) {
        const m = r.mirrors.mirrors[0];
        const c = m ? hexToWorld(m.q, m.r) : null;
        if (c && withinRange(h.x, h.y, c.x, c.y, 2400)) cue('inscription');
      }
    }
  }
  // (Chapter 5's people keep their own places: battle/vigilia.ts.)
  if (!r.hermit?.vela) placePeople(s, w);
};

/** Its nightmares move by its rule (true: handled here); a stunned one is left to the common rule. */
export const litterHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w || (e.stun > 0 && !w.pas)) return false;
  if (e.cd > 0) e.cd--;
  // Chapter 2's own: the slingers across the fog, the mangonel, the gargoyles going to a plank (battle/pasarela.ts).
  if (w.pas && pasHold(s, e, px)) return true;
  // Chapter 3's own: the moths at the braziers, the ash watcher (battle/nieve.ts).
  if (w.nev && nevHold(s, e, px)) return true;
  // Chapter 4's own: the Doliente and her tongues, the limpets going for a mirror (battle/ermita.ts).
  if (w.erm && ermHold(s, e, px)) return true;
  if (e.stun > 0) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const l = w.limpets.length ? w.limpets.find((x) => x.id === e.id) : undefined;
  if (l) {
    // Crawling to the stretcher; stuck on it, round its edge; peeled, a leap at the hero; on the hero, at its feet.
    if (l.on === 0) {
      e.anim = 'walk';
      if (stepToward(e, w.x, w.y, px(e)) || withinRange(e.x, e.y, w.x, w.y, 450)) {
        l.on = 1;
        l.t = s.tick;
        ev(s, { t: 'senda', kind: 'stick', npc: '', x: e.x, y: e.y, n: 1 });
        sendaCue(s, 'limpet');
      }
    } else if (l.on === 1) {
      const a = (e.id * 167) % 1024;
      e.x = w.x + idiv(cosA(a) * 380, 10000);
      e.y = w.y + idiv(sinA(a) * 300, 10000);
      e.anim = 'attack';
    } else if (l.on === 3) {
      if (!h) l.on = 0;
      else if (stepToward(e, h.x, h.y, imax(120, px(e) * 6)) || s.tick - l.t >= (r.limpet?.leap ?? 6) * 3) {
        l.on = 2;
        l.t = s.tick;
        ev(s, { t: 'senda', kind: 'stick', npc: '', x: e.x, y: e.y, n: 2 });
        sendaCue(s, 'leap');
      }
    } else if (l.on === 2) {
      if (!h) l.on = 0;
      else {
        const a = (e.id * 211) % 1024;
        e.x = h.x + idiv(cosA(a) * 260, 10000);
        e.y = h.y + idiv(sinA(a) * 200, 10000);
        e.anim = 'attack';
      }
    }
    return true;
  }
  const { hero, npc } = prey(s, r, e, h);
  const reach = NIGHTMARES[e.kind].reach;
  if (hero) {
    if (withinRange(e.x, e.y, hero.x, hero.y, reach + 150)) attackHero(s, e, hero);
    else {
      e.anim = 'walk';
      stepToward(e, hero.x, hero.y, px(e));
    }
    return true;
  }
  if (!npc) {
    e.anim = 'idle';
    return true;
  }
  if (withinRange(e.x, e.y, npc.x, npc.y, imin(reach + 150, r.blow.range))) {
    e.facing = iatan2(npc.y - e.y, npc.x - e.x);
    if (e.cd === 0) {
      e.cd = e.reload;
      e.anim = 'attack';
      e.animT = 0;
    }
    return true;
  }
  e.anim = 'walk';
  stepToward(e, npc.x, npc.y, px(e));
  return true;
};

/** How far along: the way's points passed (the chapel: its lamp). */
export const litterGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w) return { done: 0, need: 1 };
  const g = nevGoal(s) ?? ermGoal(s);
  if (g) return g;
  if (r.kind === 'chapel') return { done: w.lit >= 0 ? 1 : 0, need: 1 };
  if (w.done >= 0) return { done: r.way.length - 1, need: r.way.length - 1 };
  return { done: w.seg, need: imax(1, r.way.length - 1) };
};

/** Won at the end of the way (the chapel: its door open after the lamp); lost with Tessa out or both carriers down. */
export const litterResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w) return null;
  const t = person(s, 'tessa');
  if (t && t.hp <= 0) return 'lost';
  const [p, l] = carriers(s);
  if (p && l && p.hp <= 0 && l.hp <= 0 && !r.hermit?.vela) return 'lost';
  if (pasResult(s)) return 'lost';
  const nr = nevResult(s) ?? ermResult(s);
  if (nr) return nr;
  if (r.kind === 'chapel') {
    if (w.lit >= 0 && r.mirrors && s.tick >= w.lit + r.mirrors.open) {
      if (s.tick === w.lit + r.mirrors.open) {
        const c = hexToWorld(r.mirrors.lamp.q, r.mirrors.lamp.r);
        ev(s, { t: 'senda', kind: 'door', npc: '', x: c.x, y: c.y });
      }
      return 'won';
    }
    return null;
  }
  return w.done >= 0 && s.tick >= w.done + 12 ? 'won' : null;
};
